import React, { useState } from 'react';
import { FiRefreshCw, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import api from '../services/api';

const MarketplaceSync = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    marketplace: 'amazon',
    username: '',
    password: '',
    date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    date_to: new Date().toISOString().split('T')[0], // today
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSync = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      console.log('Sending sync request with data:', formData);
      const response = await api.post('/marketplace/scrape-sync.php?action=sync-all', formData);
      
      console.log('Full response:', response);
      console.log('Response data:', response.data);
      console.log('Response data.data:', response.data.data);
      
      if (response.data.success) {
        setResult(response.data.data);
        console.log('Result set to:', response.data.data);
        
        // Show warning if in demo mode
        if (response.data.data.note) {
          setError(response.data.data.note);
        }
      } else {
        setError(response.data.message || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      setError(err.response?.data?.message || 'Scraping failed. Please check your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMarketplaceLabel = (marketplace) => {
    const labels = {
      amazon: 'Amazon Seller Central',
      flipkart: 'Flipkart Seller Hub',
      meesho: 'Meesho Supplier Panel'
    };
    return labels[marketplace] || marketplace;
  };

  const getUsernameLabel = (marketplace) => {
    const labels = {
      amazon: 'Email or Phone',
      flipkart: 'Username or Email',
      meesho: 'Phone Number'
    };
    return labels[marketplace] || 'Username';
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
          Marketplace Data Sync
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Sync orders, shipments, and payments from your seller portal
        </p>
      </div>

      {/* Sync Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
        <form onSubmit={handleSync} className="space-y-6">
          {/* Marketplace Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Marketplace
            </label>
            <select
              name="marketplace"
              value={formData.marketplace}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="amazon">Amazon Seller Central</option>
              <option value="flipkart">Flipkart Seller Hub</option>
              <option value="meesho">Meesho Supplier Panel</option>
            </select>
          </div>

          {/* Credentials */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {getUsernameLabel(formData.marketplace)}
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder={`Enter your ${getUsernameLabel(formData.marketplace)}`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                placeholder="Enter your password"
                required
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Date
              </label>
              <input
                type="date"
                name="date_from"
                value={formData.date_from}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Date
              </label>
              <input
                type="date"
                name="date_to"
                value={formData.date_to}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
          </div>

          {/* Warning Box */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-300 px-4 py-3 rounded-lg">
            <p className="text-sm font-semibold mb-1">⚠️ Security Notice</p>
            <ul className="text-xs list-disc list-inside space-y-1">
              <li>Your credentials are used only for this sync session</li>
              <li>They are NOT stored in the database</li>
              <li>Always use HTTPS in production</li>
              <li>Make sure you're logged out from other devices</li>
            </ul>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FiRefreshCw className="animate-spin" />
                Scraping {getMarketplaceLabel(formData.marketplace)}...
              </>
            ) : (
              <>
                <FiRefreshCw />
                Sync from {getMarketplaceLabel(formData.marketplace)}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6 flex items-start gap-2">
          <FiAlertCircle className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 mb-4">
            <FiCheckCircle className="text-green-500 text-xl" />
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              Sync Completed Successfully
            </h3>
          </div>

          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">Marketplace</p>
              <p className="text-xl font-bold text-green-600">
                {getMarketplaceLabel(result.marketplace)}
              </p>
            </div>

            {/* Synced Data */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Orders Synced</p>
                <p className="text-2xl font-bold text-blue-600">
                  {result.results.synced.orders || 0}
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Shipments Synced</p>
                <p className="text-2xl font-bold text-purple-600">
                  {result.results.synced.shipments || 0}
                </p>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Payments Synced</p>
                <p className="text-2xl font-bold text-orange-600">
                  {result.results.synced.payments || 0}
                </p>
              </div>
            </div>

            {/* Total */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Records Synced</p>
              <p className="text-3xl font-bold text-gray-800 dark:text-white">
                {result.total_synced}
              </p>
            </div>

            {/* Errors (if any) */}
            {result.results.errors && Object.keys(result.results.errors).length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <p className="text-sm font-semibold text-red-600 mb-2">Partial Errors</p>
                <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                  {Object.entries(result.results.errors).map(([type, message]) => (
                    <li key={type}>
                      <strong>{type}:</strong> {message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 px-4 py-3 rounded-lg">
        <p className="text-sm font-semibold mb-2">ℹ️ How It Works</p>
        <ol className="text-xs list-decimal list-inside space-y-1">
          <li>System logs into your seller portal using provided credentials</li>
          <li>Scrapes orders, shipments, and payments from your dashboard</li>
          <li>Saves all data to your local database</li>
          <li>Credentials are NOT stored - session is deleted after sync</li>
        </ol>
      </div>
    </div>
  );
};

export default MarketplaceSync;
